import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import {
  cancelCatalogImport,
  getCatalogImportLogs,
  getCatalogServiceStatus,
  startCatalogImport,
} from "../services/catalogMappingService";
import {
  appendImportLogs,
  formatActivitySeparator,
  formatJobSeparator,
} from "../utils/catalogImportLogUtils";
import {
  getLogCursor,
  resetLogCursor,
  setLogCursor,
} from "../utils/catalogImportLogCursor";
import {
  clearImportLogStore,
  getImportLogLines,
  setImportLogLines,
  subscribeImportLogStore,
} from "../utils/catalogImportLogStore";

const STATUS_POLL_MS = 10000;
const LOG_POLL_MS = 2000;

export default function useCatalogImportJob({ onComplete, onError } = {}) {
  const [activeJobId, setActiveJobId] = useState(null);
  const [importRunning, setImportRunning] = useState(false);
  const [scheduledImportActive, setScheduledImportActive] = useState(false);
  const [activityRunning, setActivityRunning] = useState(false);
  const [stoppingImport, setStoppingImport] = useState(false);
  const [jobStatus, setJobStatus] = useState(null);
  const [scheduleStatus, setScheduleStatus] = useState(null);
  const [connectedDatabaseName, setConnectedDatabaseName] = useState("unknown");
  const [logDialogOpen, setLogDialogOpen] = useState(false);
  const logLines = useSyncExternalStore(subscribeImportLogStore, getImportLogLines, getImportLogLines);
  const logContainerRef = useRef(null);
  const localJobRef = useRef(false);
  const completedJobRef = useRef(null);
  const jobDoneWaitersRef = useRef(new Map());
  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);
  const stateRef = useRef({});

  onCompleteRef.current = onComplete;
  onErrorRef.current = onError;

  stateRef.current = {
    activeJobId,
    jobStatus,
    importRunning,
    activityRunning,
    localJobRef,
    completedJobRef,
    jobDoneWaitersRef,
    onCompleteRef,
    onErrorRef,
    setActiveJobId,
    setImportRunning,
    setScheduledImportActive,
    setActivityRunning,
    setStoppingImport,
    setJobStatus,
    setLogDialogOpen,
  };

  const openLogs = useCallback(() => {
    setLogDialogOpen(true);
  }, []);

  const closeLogs = useCallback(() => {
    setLogDialogOpen(false);
  }, []);

  const clearLogs = useCallback(() => {
    clearImportLogStore();
    resetLogCursor(activeJobId);
  }, [activeJobId]);

  const notifyJobDone = useCallback((jobId, status) => {
    const waiter = jobDoneWaitersRef.current.get(jobId);
    if (waiter) {
      jobDoneWaitersRef.current.delete(jobId);
      waiter.resolve(status);
    }
  }, []);

  const waitForJobDone = useCallback((jobId) => new Promise((resolve, reject) => {
    jobDoneWaitersRef.current.set(jobId, { resolve, reject });
  }), []);

  const handleTerminalJob = useCallback((jobId, status) => {
    if (!jobId || completedJobRef.current === jobId) {
      notifyJobDone(jobId, status);
      return;
    }
    completedJobRef.current = jobId;
    setImportRunning(false);
    setScheduledImportActive(false);
    setActivityRunning(false);
    setStoppingImport(false);
    localJobRef.current = false;
    notifyJobDone(jobId, status);
    if (onCompleteRef.current) {
      void onCompleteRef.current(status);
    }
  }, [notifyJobDone]);

  const ingestJobLogs = useCallback(async (jobId, options = {}) => {
    if (!jobId) {
      return null;
    }
    const logStatus = await getCatalogImportLogs(jobId, getLogCursor(jobId));
    if (typeof logStatus?.log_count === "number") {
      setLogCursor(jobId, logStatus.log_count);
    }
    if (logStatus.logs?.length) {
      setImportLogLines((previous) => appendImportLogs(previous, logStatus.logs, options));
    }
    setJobStatus((previous) => ({ ...previous, ...logStatus }));
    return logStatus;
  }, []);

  const runTrackedAction = useCallback(async (action, {
    separator,
    openDialog = true,
    progressLabel = "Operazione in corso",
  } = {}) => {
    if (openDialog) {
      setLogDialogOpen(true);
    }
    if (separator) {
      setImportLogLines((previous) => appendImportLogs(previous, [], { separator }));
    }
    try {
      const result = await action();
      if (result?.job_id) {
        resetLogCursor(result.job_id);
        setLogCursor(result.job_id, 0);
        setActiveJobId(result.job_id);
        setActivityRunning(true);
        setJobStatus({
          status: "RUNNING",
          job_id: result.job_id,
          current_step_label: progressLabel,
          current_step_percent: 0,
          total: 1,
          processed: 0,
        });
        await waitForJobDone(result.job_id);
        setActivityRunning(false);
      }
      return result;
    } catch (error) {
      setActivityRunning(false);
      setImportLogLines((previous) => appendImportLogs(previous, [{
        timestamp: new Date().toISOString(),
        level: "ERROR",
        message: error.message,
      }]));
      throw error;
    }
  }, [waitForJobDone]);

  const startJob = useCallback(async (vendorIds, options = {}) => {
    setLogDialogOpen(true);
    setImportLogLines((previous) => appendImportLogs(previous, [], { separator: formatJobSeparator() }));
    try {
      setStoppingImport(false);
      localJobRef.current = true;
      completedJobRef.current = null;
      const result = await startCatalogImport(vendorIds, {
        useStoredFeed: Boolean(options.useStoredFeed),
        importType: options.importType || "manual_single",
      });
      resetLogCursor(result.job_id);
      setLogCursor(result.job_id, 0);
      setActiveJobId(result.job_id);
      setImportRunning(true);
      setScheduledImportActive(false);
      setJobStatus({
        status: "RUNNING",
        job_id: result.job_id,
        vendor_ids: vendorIds || [],
        processed: 0,
        total: result.vendor_count || (vendorIds?.length ?? 0),
        current_step_percent: 0,
        current_step_label: "Avvio job",
        ...result,
      });
      return result;
    } catch (error) {
      localJobRef.current = false;
      setImportRunning(false);
      setStoppingImport(false);
      throw error;
    }
  }, []);

  const stopJob = useCallback(async () => {
    const jobId = activeJobId || jobStatus?.job_id;
    if (!jobId || stoppingImport) {
      return;
    }
    setStoppingImport(true);
    try {
      await cancelCatalogImport(jobId);
      setJobStatus((previous) => ({
        ...previous,
        cancel_requested: true,
        current_step_label: "Arresto in corso...",
      }));
    } catch (error) {
      setStoppingImport(false);
      if (onErrorRef.current) {
        onErrorRef.current(error);
      }
    }
  }, [activeJobId, jobStatus?.job_id, stoppingImport]);

  useEffect(() => {
    let cancelled = false;

    const applyStatus = (status) => {
      const {
        setActiveJobId: setJobId,
        setImportRunning: setRunning,
        setScheduledImportActive: setScheduled,
        setJobStatus: setStatus,
        setLogDialogOpen: setLogsOpen,
        localJobRef: localRef,
        activityRunning: activityActive,
      } = stateRef.current;

      const serverJobId = status.job_id || null;
      const serverActive = Boolean(status.active && serverJobId);
      const scheduledActive = Boolean(status.scheduled_running);

      if (serverActive) {
        setJobId(serverJobId);
        setRunning(true);
        setScheduled(scheduledActive);
        setStatus(status);
        if (scheduledActive) {
          setLogsOpen(true);
        }
      } else if (!localRef.current && !activityActive) {
        setRunning(false);
        setScheduled(false);
        if (status.status && status.status !== "IDLE") {
          setStatus(status);
        }
      }

      const terminalJobId = serverJobId || stateRef.current.activeJobId;
      if (terminalJobId && status.done) {
        handleTerminalJob(terminalJobId, status);
      }
    };

    const pollStatus = async () => {
      try {
        const payload = await getCatalogServiceStatus(stateRef.current.activeJobId || undefined);
        if (cancelled) {
          return;
        }
        if (payload?.schedule) {
          setScheduleStatus(payload.schedule);
        }
        if (payload?.database?.name) {
          setConnectedDatabaseName(payload.database.name);
        }
        applyStatus(payload?.import_job || payload);
      } catch (error) {
        if (!cancelled && onErrorRef.current) {
          onErrorRef.current(error);
        }
      }
    };

    pollStatus();
    const timer = setInterval(pollStatus, STATUS_POLL_MS);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [handleTerminalJob]);

  const progressActive = importRunning || activityRunning || scheduledImportActive;

  useEffect(() => {
    if (!progressActive) {
      return undefined;
    }

    let cancelled = false;

    const pollLogs = async () => {
      const jobId = stateRef.current.activeJobId || stateRef.current.jobStatus?.job_id;
      if (!jobId) {
        return;
      }

      try {
        const logStatus = await getCatalogImportLogs(jobId, getLogCursor(jobId));
        if (cancelled) {
          return;
        }

        if (typeof logStatus.log_count === "number") {
          setLogCursor(jobId, logStatus.log_count);
        }

        if (logStatus.logs?.length) {
          setImportLogLines((previous) => appendImportLogs(previous, logStatus.logs));
        }

        setJobStatus((previous) => ({
          ...previous,
          ...logStatus,
          job_id: jobId,
        }));

        if (logStatus.done) {
          handleTerminalJob(jobId, logStatus);
        }
      } catch (error) {
        if (!cancelled && onErrorRef.current) {
          onErrorRef.current(error);
        }
      }
    };

    pollLogs();
    const timer = setInterval(pollLogs, LOG_POLL_MS);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [progressActive, handleTerminalJob]);

  useEffect(() => {
    if (logDialogOpen && logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logLines, logDialogOpen, jobStatus]);

  const hasLogs = logLines.length > 0 || Boolean(activeJobId) || activityRunning || scheduledImportActive;
  const maintenanceLocked = scheduledImportActive;

  const isVendorInRunningJob = useCallback((vendorId) => {
    if (!importRunning && !scheduledImportActive) {
      return false;
    }
    if (jobStatus?.vendor_ids?.length) {
      return jobStatus.vendor_ids.some((id) => Number(id) === Number(vendorId));
    }
    if (jobStatus?.current_vendor_id != null) {
      return Number(jobStatus.current_vendor_id) === Number(vendorId);
    }
    return false;
  }, [importRunning, scheduledImportActive, jobStatus]);

  return {
    activeJobId,
    importRunning,
    scheduledImportActive,
    maintenanceLocked,
    activityRunning,
    progressActive,
    stoppingImport,
    jobStatus,
    scheduleStatus,
    connectedDatabaseName,
    logLines,
    logDialogOpen,
    logContainerRef,
    hasLogs,
    startJob,
    stopJob,
    openLogs,
    closeLogs,
    clearLogs,
    runTrackedAction,
    ingestJobLogs,
    formatActivitySeparator,
    isVendorInRunningJob,
  };
}
